import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeAlert, Eye, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'paid') return 'bg-emerald-100 text-emerald-800';
  if (s === 'overdue') return 'bg-red-100 text-red-800';
  if (s === 'partial') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
}

export default function BudgetCredit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    branch_id: 'all',
    month: currentMonth(),
    year: currentYear(),
  });
  const [open, setOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editing, setEditing] = useState(null);
  const [paymentItem, setPaymentItem] = useState(null);
  const [form, setForm] = useState({
    branch_id: 'all',
    customer_name: '',
    phone: '',
    amount: '',
    due_date: new Date().toISOString().slice(0, 10),
    description: '',
    currency: user?.default_currency || 'UGX',
  });
  const [paymentForm, setPaymentForm] = useState({
    amount_paid: '',
    payment_date: new Date().toISOString().slice(0, 16),
    notes: '',
    create_transaction: true,
  });

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        status: nextFilters.status && nextFilters.status !== 'all' ? nextFilters.status : undefined,
        month: nextFilters.month || undefined,
        year: nextFilters.year || undefined,
      };
      if (nextFilters.branch_id && nextFilters.branch_id !== 'all') params.branch_id = nextFilters.branch_id;
      const res = await api.get('/budget/credit', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load credit');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const singleBranch = activeBranches.length <= 1 || !!data?.single_branch;
  const credits = Array.isArray(data?.credits) ? data.credits : [];
  const summary = data?.summary || {};
  const currency = user?.default_currency || 'UGX';
  const canManage = !!data?.can_manage;

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const resetForm = () => {
    setForm({
      branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || 'all') : 'all',
      customer_name: '',
      phone: '',
      amount: '',
      due_date: new Date().toISOString().slice(0, 10),
      description: '',
      currency: user?.default_currency || 'UGX',
    });
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      branch_id: item.branch_id || (singleBranch ? (activeBranches[0]?.id || branches[0]?.id || 'all') : 'all'),
      customer_name: item.customer_name || '',
      phone: item.phone || '',
      amount: item.total_amount ?? '',
      due_date: item.due_date ? String(item.due_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
      description: item.description || '',
      currency: item.currency || user?.default_currency || 'UGX',
    });
    setOpen(true);
  };

  const openPayment = (item) => {
    setPaymentItem(item);
    setPaymentForm({
      amount_paid: '',
      payment_date: new Date().toISOString().slice(0, 16),
      notes: '',
      create_transaction: true,
    });
    setPaymentOpen(true);
  };

  const saveCredit = async () => {
    if (!form.customer_name.trim()) return toast.error('Customer name is required.');
    if (Number(form.amount || 0) <= 0) return toast.error('Amount must be greater than zero.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || form.branch_id || 'all') : form.branch_id,
        amount: Number(form.amount || 0),
      };
      if (editing?.id) {
        await api.put(`/budget/credit/${editing.id}`, payload);
        toast.success('Credit updated');
      } else {
        await api.post('/budget/credit', payload);
        toast.success('Credit added');
      }
      setOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to save credit');
    } finally {
      setSaving(false);
    }
  };

  const savePayment = async () => {
    if (!paymentItem) return;
    if (Number(paymentForm.amount_paid || 0) <= 0) return toast.error('Payment amount must be greater than zero.');
    setSaving(true);
    try {
      await api.post(`/budget/credit/${paymentItem.id}/payment`, {
        amount_paid: Number(paymentForm.amount_paid || 0),
        payment_date: paymentForm.payment_date,
        notes: paymentForm.notes,
        create_transaction: paymentForm.create_transaction,
      });
      toast.success('Credit payment recorded');
      setPaymentOpen(false);
      setPaymentItem(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const deleteCredit = async (item) => {
    if (!window.confirm('Delete this credit record?')) return;
    try {
      await api.delete(`/budget/credit/${item.id}`);
      toast.success('Credit deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to delete credit');
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <BadgeAlert size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Credit</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={load}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Credit
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Track debtors, partial payments, and overdue balances.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={load}><RefreshCw size={16} className="mr-1.5" /> Refresh</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openCreate}><Plus size={16} className="mr-1.5" /> Add Credit</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Total Outstanding</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(summary.total_outstanding || 0, currency)}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Total Overdue</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(summary.total_overdue || 0, currency)}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Collected This Month</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(summary.total_collected_this_month || 0, currency)}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Number of Debtors</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{summary.number_of_debtors || 0}</p></CardContent></Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <Select value={filters.status} onValueChange={(value) => updateFilter('status', value)}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            {!singleBranch ? (
              <Select value={filters.branch_id} onValueChange={(value) => updateFilter('branch_id', value)}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600">
                {activeBranches[0]?.name || 'Main Branch'}
              </div>
            )}
            <Input type="number" min="1" max="12" value={filters.month} onChange={(e) => updateFilter('month', e.target.value)} placeholder="Month" className="bg-white" />
            <Input type="number" min="2020" max="2100" value={filters.year} onChange={(e) => updateFilter('year', e.target.value)} placeholder="Year" className="bg-white" />
            <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-500">Credit filters keep overdue balances visible.</div>
          </div>
        </CardHeader>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Credit List</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{credits.length} item(s)</Badge>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-3 pr-4 font-medium">Customer Name</th>
                <th className="py-3 pr-4 font-medium">Phone</th>
                <th className="py-3 pr-4 font-medium">Total Amount</th>
                <th className="py-3 pr-4 font-medium">Amount Paid</th>
                <th className="py-3 pr-4 font-medium">Balance</th>
                <th className="py-3 pr-4 font-medium">Due Date</th>
                <th className="py-3 pr-4 font-medium">Status</th>
                <th className="py-3 pr-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {credits.map((item) => (
                <tr key={item.id} className="border-b border-slate-50 last:border-0">
                  <td className="py-3 pr-4 text-slate-900 font-medium">{item.customer_name}</td>
                  <td className="py-3 pr-4 text-slate-700">{item.phone || '-'}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.total_amount, item.currency || currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.amount_paid, item.currency || currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.balance, item.currency || currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{formatDate(item.due_date)}</td>
                  <td className="py-3 pr-4"><Badge className={statusTone(item.status)}>{item.status || 'pending'}</Badge></td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="border-slate-200" onClick={() => setViewItem(item)}>
                        <Eye size={14} className="mr-1" /> View
                      </Button>
                      {canManage ? (
                        <>
                          <Button size="sm" variant="outline" className="border-slate-200" onClick={() => openEdit(item)}>Edit</Button>
                          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => openPayment(item)}>Add Payment</Button>
                          <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteCredit(item)}><Trash2 size={14} /></Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {credits.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-500">No credit records yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Credit' : 'Add Credit'}</DialogTitle>
            <DialogDescription>Track debtors and collect partial payments over time.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {!singleBranch ? (
              <div className="grid gap-2">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={(value) => setForm((prev) => ({ ...prev, branch_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {(activeBranches.length ? activeBranches : branches).map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Customer Name</Label>
                <Input value={form.customer_name} onChange={(e) => setForm((prev) => ({ ...prev, customer_name: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} />
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm((prev) => ({ ...prev, due_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Optional note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveCredit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Credit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Credit Payment</DialogTitle>
            <DialogDescription>Apply payment and update the balance automatically.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Amount Paid</Label>
              <Input type="number" value={paymentForm.amount_paid} onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount_paid: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>Payment Date</Label>
              <Input type="datetime-local" value={paymentForm.payment_date} onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_date: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Input value={paymentForm.notes} onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Optional" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={paymentForm.create_transaction} onChange={(e) => setPaymentForm((prev) => ({ ...prev, create_transaction: e.target.checked }))} />
              Create income transaction automatically
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setPaymentOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={savePayment} disabled={saving}>
              {saving ? 'Saving...' : 'Save Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewItem} onOpenChange={(open) => !open && setViewItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Credit Details</DialogTitle>
            <DialogDescription>View credit summary and status.</DialogDescription>
          </DialogHeader>
          {viewItem ? (
            <div className="grid gap-3 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-slate-500">Customer</p><p className="font-medium">{viewItem.customer_name}</p></div>
                <div><p className="text-slate-500">Phone</p><p className="font-medium">{viewItem.phone || '-'}</p></div>
                <div><p className="text-slate-500">Total Amount</p><p className="font-medium">{money(viewItem.total_amount, viewItem.currency || currency)}</p></div>
                <div><p className="text-slate-500">Amount Paid</p><p className="font-medium">{money(viewItem.amount_paid, viewItem.currency || currency)}</p></div>
                <div><p className="text-slate-500">Balance</p><p className="font-medium">{money(viewItem.balance, viewItem.currency || currency)}</p></div>
                <div><p className="text-slate-500">Due Date</p><p className="font-medium">{formatDate(viewItem.due_date)}</p></div>
              </div>
              <div>
                <p className="text-slate-500 mb-1">Description</p>
                <p>{viewItem.description || '-'}</p>
              </div>
              <div><Badge className={statusTone(viewItem.status)}>{viewItem.status || 'pending'}</Badge></div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setViewItem(null)}>Close</Button>
            {canManage && viewItem ? <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => { setViewItem(null); openPayment(viewItem); }}>Add Payment</Button> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
